let activeModal: Modal | null = null;
let layer: HTMLElement | null = null;

function modalLayer(): HTMLElement {
    if (layer?.isConnected) return layer;
    layer = document.createElement('div');
    layer.id = 'modalLayer';
    (document.getElementById('gameContainer') ?? document.body).appendChild(layer);
    return layer;
}

interface ModalOptions {
    title?: string;
    variant?: string;
    dismissible?: boolean;
    onClose?: (() => void) | null;
}

export class Modal {
    isOpen = false;
    readonly body: HTMLElement;

    private title: string;
    private readonly dismissible: boolean;
    private readonly onClose: (() => void) | null;
    private readonly scrim: HTMLElement;
    private readonly panel: HTMLElement;
    private readonly plaque: HTMLElement;
    private opener: Element | null = null;
    private readonly onKeydown: (e: KeyboardEvent) => void;

    constructor({
        title = '',
        variant = '',
        dismissible = true,
        onClose = null,
    }: ModalOptions = {}) {
        this.title = title;
        this.dismissible = dismissible;
        this.onClose = onClose;

        this.scrim = document.createElement('div');
        this.scrim.className = 'modal-scrim';

        this.panel = document.createElement('div');
        this.panel.className = `modal-panel${variant ? ` modal-panel--${variant}` : ''}`;
        this.panel.setAttribute('role', 'dialog');
        this.panel.setAttribute('aria-modal', 'true');
        this.panel.tabIndex = -1;

        this.plaque = document.createElement('div');
        this.plaque.className = 'modal-title';
        this.applyTitle();

        if (variant === 'options') {
            const header = document.createElement('div');
            header.className = 'modal-header';
            header.appendChild(this.plaque);

            if (dismissible) {
                const close = document.createElement('button');
                close.type = 'button';
                close.className = 'modal-close';
                close.setAttribute('aria-label', 'Close');
                close.textContent = '✕';
                close.addEventListener('click', () => this.close());
                header.appendChild(close);
            }

            this.panel.appendChild(header);
        } else {
            this.panel.appendChild(this.plaque);
            if (dismissible) {
                const close = document.createElement('button');
                close.type = 'button';
                close.className = 'modal-close';
                close.setAttribute('aria-label', 'Close');
                close.textContent = '✕';
                close.addEventListener('click', () => this.close());
                this.panel.appendChild(close);
            }
        }

        this.body = document.createElement('div');
        this.body.className = 'modal-body';
        this.panel.appendChild(this.body);

        this.scrim.appendChild(this.panel);

        if (dismissible) {
            this.scrim.addEventListener('pointerdown', (e) => {
                if (e.target === this.scrim) this.close();
            });
        }

        this.onKeydown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && this.dismissible) this.close();
        };
    }

    setTitle(title: string): void {
        this.title = title;
        this.applyTitle();
    }

    private applyTitle(): void {
        this.plaque.textContent = this.title;
        this.plaque.classList.toggle('is-empty', !this.title);
        if (this.title) this.panel.setAttribute('aria-label', this.title);
        else this.panel.removeAttribute('aria-label');
    }

    open(): void {
        if (this.isOpen) return;
        if (activeModal && activeModal !== this) activeModal.close();

        this.opener = document.activeElement;
        modalLayer().appendChild(this.scrim);
        document.addEventListener('keydown', this.onKeydown);
        this.isOpen = true;
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        activeModal = this;

        this.panel.focus({ preventScroll: true });
    }

    close(): void {
        if (!this.isOpen) return;

        document.removeEventListener('keydown', this.onKeydown);
        this.scrim.remove();
        this.isOpen = false;
        if (activeModal === this) activeModal = null;

        if (this.opener instanceof HTMLElement && this.opener.isConnected) {
            this.opener.focus({ preventScroll: true });
        }
        this.opener = null;

        this.onClose?.();
    }
}
